import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useOnboarding } from '../../hooks/useOnboarding';

export interface OnboardingStep {
  /** CSS selector for the target element, e.g. '[data-tour="app-screens-version"]' */
  target: string;
  title: string;
  body: React.ReactNode;
  /** Bu adım aktif olduğunda bir kere çağrılır (örn. bir modal'ı açmak için). */
  onEnter?: () => void;
}

export interface OnboardingTourProps {
  tourId: string;
  steps: OnboardingStep[];
  /** Otomatik başlasın mı (ilk ziyarette hasSeen=false ise). Default true. */
  autoStart?: boolean;
}

const Z_OVERLAY = 9996;
const Z_CARD = 9997;
const CARD_GAP = 12;
const MAX_FIND_ATTEMPTS = 20;

/**
 * Sayfa üzerinde adım adım rehberlik eden spotlight coachmark.
 * Modal değildir — arkaplan tıklanabilir kalır, sadece görsel rehberlik sağlar.
 * Header'daki "Yardım / Onboarding'i yeniden başlat" menüsü `buildmate:restart-onboarding`
 * global event'ini dispatch eder; sayfada mount edilmiş olan tur bunu dinleyip kendini resetler.
 */
const OnboardingTour: React.FC<OnboardingTourProps> = ({ tourId, steps, autoStart = true }) => {
  const { t } = useTranslation();
  const { hasSeen, markSeen, reset } = useOnboarding(tourId);
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [cardPos, setCardPos] = useState<{ top: number; left: number } | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // İlk ziyarette otomatik başlat.
  useEffect(() => {
    if (autoStart && !hasSeen) {
      setStepIndex(0);
      setActive(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Header'dan "yeniden başlat" tetiklendiğinde bu sayfada mount edilmiş tur kendini resetler.
  useEffect(() => {
    const handler = () => {
      reset();
      setStepIndex(0);
      setActive(true);
    };
    window.addEventListener('buildmate:restart-onboarding', handler);
    return () => window.removeEventListener('buildmate:restart-onboarding', handler);
  }, [reset]);

  const finish = () => {
    setActive(false);
    setRect(null);
    setCardPos(null);
    markSeen();
  };

  // Aktif adımın hedefini bul ve konumunu ölç; henüz DOM'da yoksa birkaç kez tekrar dener.
  useEffect(() => {
    if (!active) return;
    const step = steps[stepIndex];
    if (!step) {
      finish();
      return;
    }
    step.onEnter?.();
    let cancelled = false;
    let attempts = 0;
    const tryFind = () => {
      if (cancelled) return;
      const el = document.querySelector(step.target) as HTMLElement | null;
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        requestAnimationFrame(() => {
          if (!cancelled) setRect(el.getBoundingClientRect());
        });
      } else if (attempts < MAX_FIND_ATTEMPTS) {
        attempts += 1;
        setTimeout(tryFind, 100);
      } else if (stepIndex < steps.length - 1) {
        setStepIndex(i => i + 1);
      } else {
        finish();
      }
    };
    tryFind();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, stepIndex]);

  // Kart konumunu hedefe göre hesapla (altına, yer yoksa üstüne çevir).
  useEffect(() => {
    if (!rect) {
      setCardPos(null);
      return;
    }
    setCardPos({ top: rect.bottom + CARD_GAP, left: rect.left });
  }, [rect]);

  useEffect(() => {
    if (!rect || !cardPos) return;
    const card = cardRef.current;
    if (!card) return;
    const h = card.offsetHeight;
    const w = card.offsetWidth;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < h + CARD_GAP && rect.top > h + CARD_GAP;
    const top = openAbove ? rect.top - h - CARD_GAP : rect.bottom + CARD_GAP;
    const left = Math.min(Math.max(8, rect.left), window.innerWidth - w - 8);
    if (Math.round(top) !== Math.round(cardPos.top) || Math.round(left) !== Math.round(cardPos.left)) {
      setCardPos({ top, left });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rect, stepIndex]);

  // Scroll/resize'da hedefi yeniden ölç.
  useEffect(() => {
    if (!active) return;
    const step = steps[stepIndex];
    if (!step) return;
    const update = () => {
      const el = document.querySelector(step.target) as HTMLElement | null;
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [active, stepIndex, steps]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish();
    };
    if (active) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  if (!active || !rect || typeof document === 'undefined') return null;

  const step = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;
  const padding = 6;

  return createPortal(
    <>
      {/* Spotlight: hedefin etrafında yarı saydam kesim, arkaplan tıklanabilir kalır */}
      <div
        aria-hidden
        className="fixed rounded-lg transition-all duration-200 pointer-events-none"
        style={{
          top: rect.top - padding,
          left: rect.left - padding,
          width: rect.width + padding * 2,
          height: rect.height + padding * 2,
          boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.55)',
          zIndex: Z_OVERLAY,
          outline: '2px solid rgb(124, 66, 255)',
          outlineOffset: '2px',
        }}
      />
      {cardPos && (
        <div
          ref={cardRef}
          role="dialog"
          aria-label={step.title}
          className="fixed w-80 max-w-[calc(100vw-16px)] rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl flex flex-col"
          style={{ top: cardPos.top, left: cardPos.left, zIndex: Z_CARD }}
        >
          <div className="p-4 flex items-start gap-3">
            <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 text-sm font-semibold">
              {stepIndex + 1}
            </div>
            <div className="min-w-0 flex-1">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{step.title}</h4>
              <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">{step.body}</div>
            </div>
            <button
              type="button"
              onClick={finish}
              aria-label={t('onboarding.skip')}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="px-4 pb-4 flex items-center justify-between">
            <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">
              {stepIndex + 1}/{steps.length}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={finish}
                className="px-2.5 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                {t('onboarding.skip')}
              </button>
              {stepIndex > 0 && (
                <button
                  type="button"
                  onClick={() => setStepIndex(i => i - 1)}
                  className="px-2.5 py-1.5 text-xs font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  {t('onboarding.back')}
                </button>
              )}
              <button
                type="button"
                onClick={() => (isLast ? finish() : setStepIndex(i => i + 1))}
                className="px-2.5 py-1.5 text-xs font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700"
              >
                {isLast ? t('onboarding.finish') : t('onboarding.next')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default OnboardingTour;
